import { Box, Grid } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { makeStyles } from '@mui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/tenantStyle.js';
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PromptAlert } from '../../features/ux/RouterPrompt';
import { isValidURL } from '../../utils';
import ChipTextField from "../ChipTextField";
import { useGetTenantQuery } from 'api/tenant.api';
import { useUpdateTenantProjectSettingsMutation } from 'api/tenant.api';
import SecretInput from "../../components/CustomInput/SecretInput";

const useStyles = makeStyles(styles);

export default function ProjectSettingsTab({
  onSuccessTab,
  tab,
  setDirtyTabStatus,
}) {
  const classes = useStyles();
  const {data: tenant} = useGetTenantQuery()
  const [updateProjectSettings] = useUpdateTenantProjectSettingsMutation();

  const ctConfig = tenant?.ct_config ?? {};


  const [viewIsDirty, setViewIsDirty] = useState(false);
  const [projectKey, setProjectKey] = useState(ctConfig.project_key ?? '');
  const [clientSecret, setClientSecret] = useState(
    ctConfig.client_secret ?? ''
  );
  const [clientId, setClientId] = useState(ctConfig.client_id ?? '');
  const [authUrl, setAuthUrl] = useState(ctConfig.auth_url ?? '');
  const [scopes, setScopes] = useState(() => {
    const initialScopes = ctConfig.scopes ?? '';
    if (initialScopes.includes(',')) {
      setTimeout(() => setViewIsDirty(true), 0);
      return initialScopes.replace(/,/g, ' ').trim();
    }
    return initialScopes;
  });
  const [host, setHost] = useState(ctConfig.host ?? '');
  const [inStoreOrderType, setInStoreOrderType] = useState(
    ctConfig.order_type ?? ''
  );
  const [inStoreLineitemType, setInStoreLineitemType] = useState(
    ctConfig.lineitem_type ?? ''
  );
  const [customerType, setCustomerType] = useState(
    ctConfig.customer_type ?? ''
  );
  const [inStorePaymentType, setInStorePaymentType] = useState(
    ctConfig.payment_type ?? ''
  );
  const [transactionType, setTransactionType] = useState(
    ctConfig.transaction_type ?? ''
  );

  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [ cartKeyPrefix, setCartKeyPrefix ] = useState(tenant.cart_key_prefix);
  const [ cartKeySequenceLength, setCartKeySequenceLength] = useState(tenant.cart_key_sequence_length);
  const [errors, setErrors] = useState({
    projectKey: false,
    clientSecret: false,
    clientId: false,
    authUrl: false,
    scopes: false,
    host: false,
    inStoreOrderType: false,
    inStoreLineitemType: false,
    customerType: false,
    inStorePaymentType: false,
    transactionType: false,
  });

  const inputRef = useRef(null);

  useEffect(() => {
    const isDirty =
      projectKey !== (ctConfig?.project_key ?? '') ||
      clientSecret !== (ctConfig?.client_secret ?? '') ||
      clientId !== (ctConfig?.client_id ?? '') ||
      authUrl !== (ctConfig?.auth_url ?? '') ||
      scopes !== (ctConfig?.scopes ?? '') ||
      host !== (ctConfig?.host ?? '') ||
      inStoreOrderType !== (ctConfig?.order_type ?? '') ||
      inStoreLineitemType !== (ctConfig?.lineitem_type ?? '') ||
      customerType !== (ctConfig?.customer_type ?? '') ||
      inStorePaymentType !== (ctConfig?.payment_type ?? '') ||
      transactionType !== (ctConfig?.transaction_type ?? '');
    setViewIsDirty(isDirty);
  }, [
    projectKey,
    clientSecret,
    clientId,
    authUrl,
    scopes,
    host,
    inStoreOrderType,
    inStoreLineitemType,
    customerType,
    inStorePaymentType,
    transactionType,
    ctConfig,
  ]);

  useEffect(() => {
    if (setDirtyTabStatus) {
      setDirtyTabStatus(viewIsDirty);
    }
  }, [viewIsDirty]);

  const isValidLength = (value, minLength, maxLength) => {
    return (value?.length >= minLength && value?.length <= maxLength) || value?.length === 0;
  };

  const isValidRegex = (value, regex) => {
    return regex ? regex.test(value) : true;
  };

  const handleInvalidInput = (field, isValid) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: isValid,
    }));
    setViewIsDirty(false);
  }

  const handleInputChange =
    (setter, field, validateUrl = false, fieldMinLength = 10, fieldMaxlength = 256, regex = null, numeric = false) =>
    (event) => {
      let value = event.target.value;
      
      if (numeric && value !== '') {
        value = value.replace(/[^0-9]/g, '');
      }
      
      setter(numeric && value !== '' ? Number(value) : value);

      if (value.trim() !== '') {
        if (validateUrl) {
          const isValid = isValidURL(value);
          setErrors((prevErrors) => ({
            ...prevErrors,
            [field]: !isValid,
          }));
        } else {
          let isValid = true;
          if(field === 'scopes') {
            isValid = value?.length > 0  && isValidRegex(value, regex);
          }
          else if(field === 'cart_key_sequence_length'){
            isValid = value >=3 && value <= 12;
          }
          else{
            isValid = isValidLength(value, fieldMinLength, fieldMaxlength) && isValidRegex(value, regex);
          }
          setErrors((prevErrors) => ({
            ...prevErrors,
            [field]: !isValid,
          }));
        }
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: false,
        }));
      }

      setViewIsDirty(true);
    };

  const handleBlur = (field, value, validateUrl = false, fieldMinLength = 10, fieldMaxLength = 256, regex = null) => {
    const isEmpty = value == null || value === '' || (typeof value === 'string' && !value.trim());
    if (isEmpty) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: false,
      }));
    } else {
      if (validateUrl) {
        const isValid = isValidURL(value);
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: !isValid,
        }));
      } else {
        let isValid;
        if (field === 'cart_key_sequence_length') {
          isValid = value >= 3 && value <= 12;
        } else {
          const stringValue = String(value);
          isValid = isValidLength(stringValue, fieldMinLength, fieldMaxLength) && isValidRegex(stringValue, regex);
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: !isValid,
        }));
      }
    }
  };

  const revertForm = () => {
    setProjectKey(ctConfig.project_key ?? '');
    setClientSecret(ctConfig.client_secret ?? '');
    setClientId(ctConfig.client_id ?? '');
    setAuthUrl(ctConfig.auth_url ?? '');
    setScopes(ctConfig.scopes ?? '');
    setHost(ctConfig.host ?? '');
    setInStoreOrderType(ctConfig.order_type ?? '');
    setInStoreLineitemType(ctConfig.lineitem_type ?? '');
    setCustomerType(ctConfig.customer_type ?? '');
    setInStorePaymentType(ctConfig.payment_type ?? '');
    setTransactionType(ctConfig.transaction_type ?? '');
    setViewIsDirty(false);
    setCartKeyPrefix(tenant.cart_key_prefix ?? '');
    setCartKeySequenceLength(tenant.cart_key_sequence_length ?? '');

    setResetInputs(prev => prev + 1);
    setErrors({
      projectKey: false,
      clientSecret: false,
      clientId: false,
      authUrl: false,
      scopes: false,
      host: false,
      inStoreOrderType: false,
      inStoreLineitemType: false,
      customerType: false,
      inStorePaymentType: false,
      transactionType: false,
      cartKeyPrefix: false,
      cartKeySequenceLength: false
    });
  };

  const saveChanges = async () => {
    const updatedTenant = {
      ...tenant,
      cart_key_prefix: cartKeyPrefix,
      cart_key_sequence_length: cartKeySequenceLength,
      ct_config: {
        project_key: projectKey,
        ...(clientSecret !== '' ? {client_secret: clientSecret} : {}),
        client_id: clientId,
        auth_url: authUrl,
        scopes: scopes,
        host: host,
        order_type: inStoreOrderType,
        lineitem_type: inStoreLineitemType,
        customer_type: customerType,
        payment_type: inStorePaymentType,
        transaction_type: transactionType,
      },
    };

    try {
      await updateProjectSettings({tenantProjectSettings: updatedTenant}).unwrap();
      setClientSecret('');
      setViewIsDirty(false);
      setResetInputs(prev => prev + 1);

      toast.success('Changes saved successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });

      if (onSuccessTab) {
        onSuccessTab(tab.name);
      }
    } catch (err) {
      console.error('Error saving project settings:', err);
      toast.error('An error occurred while ' + 'trying to save the changes', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const hasErrors = useCallback(() => {
    return Object.values(errors).some((error) => error === true);
  }, [errors]);

  const [resetInputs, setResetInputs] = useState(0);

  const keyRegex = /^(?!local$|admin$|config$)[\w-]{2,36}$/i

  return (
    <>
      <GridContainer>
        {showWarningAlert && (
          <PromptAlert
            message='You have unsaved changes. Are you sure you want to discard them?'
            title='Discard Changes'
            onConfirm={() => {
              revertForm();
              setShowWarningAlert(false);
            }}
            onCancel={() => setShowWarningAlert(false)}
          />
        )}
        <Grid container md={12}>
          <GridItem md={6}>
            {/* Project key */}
            <CustomInput
              labelText='Project Key'
              id='project-key'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: projectKey,
                onChange: handleInputChange(
                  setProjectKey,
                  'projectKey',
                  false,
                  2, 
                  36,
                  keyRegex
                ),
                onBlur: () => handleBlur('projectKey', projectKey,false, 2, 36, keyRegex),
                autoComplete: 'off',
              }}
            />
            {errors.projectKey && (
              <FormHelperText error>
                Must be at least 2 characters
              </FormHelperText>
            )}
            {/* Client Secret */}
            <SecretInput
              inputId='client-secret'
              labelText='Client Secret'
              inputValue={tenant?.ct_config?.hasClientSecret}
              onValidSecret={(value) => {
                setClientSecret(value)
                handleInvalidInput('clientSecret', false)
              }}
              onInvalidSecret={() => handleInvalidInput('clientSecret', true)}
              validationRules={{ minLength: 10 }}
              resetInput={resetInputs}
            ></SecretInput>
            {/* Client ID */}
            <CustomInput
              labelText='Client ID'
              id='client-id'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: clientId,
                onChange: handleInputChange(setClientId, 'clientId'),
                onBlur: () => handleBlur('clientId', clientId),
                autoComplete: 'off',
              }}
            />
            {errors.clientId && (
              <FormHelperText error>
                Must be at least 10 characters
              </FormHelperText>
            )}
            {/* Auth URL */}
            <CustomInput
              labelText='Auth URL'
              id='auth-url'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: authUrl,
                onChange: handleInputChange(setAuthUrl, 'authUrl', true),
                onBlur: () => handleBlur('authUrl', authUrl, true),
                autoComplete: 'off',
                error: errors.authUrl,
              }}
            />
            {errors.authUrl && (
              <FormHelperText error>
                Invalid URL or length less than 10 characters
              </FormHelperText>
            )}
            {/* Host */}
            <CustomInput
              labelText='Host'
              id='host'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: host,
                onChange: handleInputChange(setHost, 'host', true),
                onBlur: () => handleBlur('host', host, true),
                autoComplete: 'off',
                error: errors.host,
              }}
            />
            {errors.host && (
              <FormHelperText error>
                Invalid URL or length less than 10 characters
              </FormHelperText>
            )}
            <ChipTextField
              labelText='Scopes'
              id='scopes'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: scopes,
                onChange: handleInputChange(setScopes, 'scopes'),
                onBlur: () => handleBlur('scopes', scopes),
                autoComplete: 'off',
              }}
            />
            {errors.scopes && (
              <FormHelperText error>
                Must be at least 10 characters
              </FormHelperText>
            )}
            {/* Cart Key Prefix */}
            <CustomInput
              labelText='Cart Key Prefix'
              id='cart_key_prefix'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: cartKeyPrefix ?? '',
                onChange: handleInputChange(
                  setCartKeyPrefix,
                  'cart_key_prefix',
                  false,
                  1,
                  9,
                  /^[0-9]+$/,
                  true
                ),
                onBlur: () => handleBlur(
                  'cart_key_prefix',
                  cartKeyPrefix,
                  false,
                  1,
                  9,
                  /^[0-9]+$/
                ),
                autoComplete: 'off',
                error: errors.cart_key_prefix,
              }}
            />
            {errors.cart_key_prefix && (
              <FormHelperText error>
                Invalid cart key prefix, must be numeric and be 1 to 9 digits long
              </FormHelperText>
            )}
            {/* Cart Key Sequence Length */}
            <CustomInput
              labelText='Cart Key Sequence Length'
              id='cart_key_sequence_length'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: cartKeySequenceLength ?? '',
                onChange: handleInputChange(
                  setCartKeySequenceLength,
                  'cart_key_sequence_length',
                  false,
                  1,
                  2,
                  null,
                  true
                ),
                onBlur: () => handleBlur(
                  'cart_key_sequence_length', 
                  cartKeySequenceLength, 
                  false,
                  1,
                  2
                ),
                autoComplete: 'off',
                error: errors.cart_key_sequence_length,
              }}
            />
            {errors.cart_key_sequence_length && (
              <FormHelperText error>
                Invalid cart key sequence length, must be a numeric value betweeen 3 and up to 12
              </FormHelperText>
            )}
          </GridItem>
          <GridItem md={6}>
            {/* In Store Order Type */}
            <CustomInput
              labelText='InStore Order Type'
              id='in-store-order-type'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: inStoreOrderType,
                onChange: handleInputChange(
                  setInStoreOrderType,
                  'inStoreOrderType',
                  false,
                  2,
                  256,
                  /^[A-Za-z0-9_-]+$/
                ),
                onBlur: () => handleBlur('inStoreOrderType', inStoreOrderType, false, 2, 256, /^[A-Za-z0-9_-]+$/),
                autoComplete: 'off',
              }}
            />
            {errors.inStoreOrderType && (
              <FormHelperText error>
                Must be between 2 and 256 characters and contain only letters, numbers, underscores, and hyphens
              </FormHelperText>
            )}

            {/* In-Store Lineitem Type */}
            <CustomInput
              labelText='InStore Lineitem Type'
              id='in-store-lineitem-type'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: inStoreLineitemType,
                onChange: handleInputChange(
                  setInStoreLineitemType,
                  'inStoreLineitemType',
                  false,
                  2,
                  256,
                  /^[A-Za-z0-9_-]+$/
                ),
                onBlur: () =>
                  handleBlur('inStoreLineitemType', inStoreLineitemType, false, 2, 256, /^[A-Za-z0-9_-]+$/),
                autoComplete: 'off',
              }}
            />
            {errors.inStoreLineitemType && (
              <FormHelperText error>
                Must be between 2 and 256 characters and contain only letters, numbers, underscores, and hyphens
              </FormHelperText>
            )}

            {/* Customer Type */}
            <CustomInput
              labelText='Customer Type'
              id='customer-type'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: customerType,
                onChange: handleInputChange(setCustomerType, 'customerType', false, 2, 256, /^[A-Za-z0-9_-]+$/),
                onBlur: () => handleBlur('customerType', customerType, false, 2, 256, /^[A-Za-z0-9_-]+$/),
                autoComplete: 'off',
              }}
            />
            {errors.customerType && (
              <FormHelperText error>
                Must be between 2 and 256 characters and contain only letters, numbers, underscores, and hyphens
              </FormHelperText>
            )}

            {/* In-Store Payment Type */}
            <CustomInput
              labelText='InStore Payment Type'
              id='in-store-payment-type'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: inStorePaymentType,
                onChange: handleInputChange(
                  setInStorePaymentType,
                  'inStorePaymentType',
                  false,
                  2,
                  256,
                  /^[A-Za-z0-9_-]+$/
                ),
                onBlur: () =>
                  handleBlur('inStorePaymentType', inStorePaymentType, false, 2, 256, /^[A-Za-z0-9_-]+$/),
                autoComplete: 'off',
              }}
            />
            {errors.inStorePaymentType && (
              <FormHelperText error>
                Must be between 2 and 256 characters and contain only letters, numbers, underscores, and hyphens
              </FormHelperText>
            )}

            {/* Transaction Type */}
            <CustomInput
              labelText='Transaction Type'
              id='transaction-type'
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: transactionType,
                onChange: handleInputChange(
                  setTransactionType,
                  'transactionType',
                  false,
                  2,
                  256,
                  /^[A-Za-z0-9_-]+$/
                ),
                onBlur: () => handleBlur('transactionType', transactionType, false, 2, 256, /^[A-Za-z0-9_-]+$/),
                autoComplete: 'off',
              }}
            />
            {errors.transactionType && (
              <FormHelperText error>
                Must be between 2 and 256 characters and contain only letters, numbers, underscores, and hyphens
              </FormHelperText>
            )}
          </GridItem>
        </Grid>
        <GridItem xs={12}>
          <Box className={classes.buttonContainer}>
            <Button
              color='primary'
              onClick={saveChanges}
              disabled={!viewIsDirty || !tab.active || hasErrors()}
              className={classes.registerButton}
            >
              Save Changes
            </Button>
            <Button
              color='secondary'
              onClick={() => setShowWarningAlert(true)}
              disabled={!viewIsDirty}
              className={classes.registerButton}
            >
              Cancel
            </Button>
          </Box>
        </GridItem>
      </GridContainer>
    </>
  );
}
